<template>
  <div class="layer-panel">
    <div class="layer-panel__header">
      <h3>
        <i class="fas fa-layer-group"></i>
        {{ $t('annotation.layers') }}
      </h3>
      <button class="btn-close" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="layer-panel__content">
      <!-- Layer List -->
      <div class="layer-list">
        <TransitionGroup name="layer-fade">
          <div
            v-for="layer in sortedLayers"
            :key="layer.id"
            :class="[
              'layer-item',
              { 'layer-item--active': layer.id === activeLayerId },
              { 'layer-item--locked': layer.locked },
            ]"
            @click="selectLayer(layer.id)"
          >
            <!-- Visibility Toggle -->
            <button
              class="layer-item__action"
              :title="layer.visible ? $t('annotation.hideLayer') : $t('annotation.showLayer')"
              @click.stop="toggleVisibility(layer.id)"
            >
              <i :class="layer.visible ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
            </button>

            <!-- Lock Toggle -->
            <button
              class="layer-item__action"
              :title="layer.locked ? $t('annotation.unlockLayer') : $t('annotation.lockLayer')"
              @click.stop="toggleLock(layer.id)"
            >
              <i :class="layer.locked ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
            </button>

            <!-- Layer Name -->
            <div 
              class="layer-item__name" 
              v-if="!editingLayerId || editingLayerId !== layer.id"
              @dblclick.stop="startRenameLayer(layer.id, layer.name)"
            >
              <span class="layer-item__color" :style="{ backgroundColor: layer.color }"></span>
              <span class="layer-item__text">{{ layer.name }}</span>
              <span v-if="layer.objectCount !== undefined" class="layer-item__count">({{ layer.objectCount }})</span>
            </div>

            <!-- Edit Name Input -->
            <input
              v-else
              v-model="editingLayerName"
              type="text"
              class="layer-item__input"
              @keyup.enter="saveLayerName(layer.id)"
              @keyup.esc="cancelEditLayerName"
              @blur="saveLayerName(layer.id)"
              @click.stop
              :ref="(el: any) => setEditInputRef(el)"
              autofocus
            />

            <!-- Layer Actions Dropdown -->
            <div class="layer-item__actions">
              <button
                class="layer-item__menu"
                @click.stop="toggleLayerMenu(layer.id)"
                :title="$t('annotation.layerActions')"
              >
                <i class="fas fa-ellipsis-v"></i>
              </button>

              <!-- Dropdown Menu -->
              <div v-if="activeMenuLayerId === layer.id" class="layer-menu" @click.stop>
                <button @click="startRenameLayer(layer.id, layer.name)">
                  <i class="fas fa-edit"></i>
                  {{ $t('annotation.rename') }}
                </button>
                <button @click="duplicateLayer(layer.id)">
                  <i class="fas fa-copy"></i>
                  {{ $t('annotation.duplicate') }}
                </button>
                <button @click="changeLayerColor(layer.id)">
                  <i class="fas fa-palette"></i>
                  {{ $t('annotation.changeColor') }}
                </button>
                <button @click="moveLayerUp(layer.id)" :disabled="isTopLayer(layer.id)">
                  <i class="fas fa-arrow-up"></i>
                  {{ $t('annotation.moveUp') }}
                </button>
                <button @click="moveLayerDown(layer.id)" :disabled="isBottomLayer(layer.id)">
                  <i class="fas fa-arrow-down"></i>
                  {{ $t('annotation.moveDown') }}
                </button>
                <hr />
                <button 
                  @click="deleteLayer(layer.id)" 
                  class="layer-menu__danger"
                  :disabled="layers.length <= 1"
                >
                  <i class="fas fa-trash"></i>
                  {{ $t('annotation.deleteLayer') }}
                </button>
              </div>
            </div>

            <!-- Opacity Slider (when active) -->
            <div v-if="layer.id === activeLayerId && showOpacityControl" class="layer-item__opacity">
              <label>{{ $t('annotation.opacity') }}: {{ Math.round(layer.opacity * 100) }}%</label>
              <input
                type="range"
                min="0"
                max="100"
                :value="layer.opacity * 100"
                @input="updateLayerOpacity(layer.id, $event)"
                @click.stop
              />
            </div>
          </div>
        </TransitionGroup>
      </div>

      <!-- Add New Layer Button -->
      <button class="layer-panel__add-btn" @click="createNewLayer">
        <i class="fas fa-plus"></i>
        {{ $t('annotation.newLayer') }}
      </button>

      <!-- Layer Controls -->
      <div class="layer-panel__controls">
        <button @click="showOpacityControl = !showOpacityControl" :class="{ active: showOpacityControl }">
          <i class="fas fa-adjust"></i>
          {{ $t('annotation.opacity') }}
        </button>
        <button @click="mergeVisibleLayers" :disabled="visibleLayersCount < 2">
          <i class="fas fa-object-group"></i>
          {{ $t('annotation.mergeLayers') }}
        </button>
      </div>
    </div>

    <!-- Color Picker Modal -->
    <div v-if="showColorPicker" class="modal-overlay" @click="showColorPicker = false">
      <div class="color-picker-dialog" @click.stop>
        <div class="color-picker-header">
          <h4>{{ $t('annotation.selectLayerColor') }}</h4>
          <button class="btn-close" @click="showColorPicker = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="color-picker-content">
          <div class="color-preset-grid">
            <button
              v-for="color in colorPresets"
              :key="color"
              class="color-preset"
              :style="{ backgroundColor: color }"
              @click="applyLayerColor(color)"
            ></button>
          </div>
          <div class="color-picker-custom">
            <label>{{ $t('annotation.customColor') }}</label>
            <input type="color" v-model="customColor" @change="applyLayerColor(customColor)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';
import type { AnnotationLayer } from '@/types/annotation';

interface Props {
  layers: AnnotationLayer[];
  activeLayerId: string | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'layer-create', name: string): void;
  (e: 'layer-delete', layerId: string): void;
  (e: 'layer-rename', layerId: string, name: string): void;
  (e: 'layer-select', layerId: string): void;
  (e: 'layer-toggle-visibility', layerId: string): void;
  (e: 'layer-toggle-lock', layerId: string): void;
  (e: 'layer-change-color', layerId: string, color: string): void;
  (e: 'layer-change-opacity', layerId: string, opacity: number): void;
  (e: 'layer-reorder', layerId: string, direction: 'up' | 'down'): void;
  (e: 'layer-duplicate', layerId: string): void;
  (e: 'layer-merge', layerIds: string[]): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();
const { notifyConfirm } = useNotifications();

// State
const editingLayerId = ref<string | null>(null);
const editingLayerName = ref('');
const activeMenuLayerId = ref<string | null>(null);
const showOpacityControl = ref(false);
const showColorPicker = ref(false);
const selectedLayerForColor = ref<string | null>(null);
const customColor = ref('#007bff');
const hasSelectedText = ref(false);

// Callback ref for edit input
const setEditInputRef = (el: any) => {
  if (el && !hasSelectedText.value) {
    nextTick(() => {
      el.focus();
      el.select();
      hasSelectedText.value = true;
    });
  }
};

// Color presets for layer color-coding
const colorPresets = [
  '#007bff', // Blue
  '#dc3545', // Red
  '#28a745', // Green
  '#ffc107', // Yellow
  '#6f42c1', // Purple
  '#fd7e14', // Orange
  '#20c997', // Teal
  '#e83e8c', // Pink
  '#6c757d', // Gray
  '#17a2b8', // Cyan
  '#343a40', // Dark
  '#f8f9fa', // Light
];

// Computed
const sortedLayers = computed(() => {
  return [...props.layers].sort((a, b) => b.order - a.order);
});

const visibleLayersCount = computed(() => {
  return props.layers.filter((l: AnnotationLayer) => l.visible).length;
});

// Methods
const selectLayer = (layerId: string) => {
  emit('layer-select', layerId);
  activeMenuLayerId.value = null;
};

const toggleVisibility = (layerId: string) => {
  emit('layer-toggle-visibility', layerId);
};

const toggleLock = (layerId: string) => {
  emit('layer-toggle-lock', layerId);
};

const createNewLayer = () => {
  const layerNumber = props.layers.length + 1;
  const defaultName = `${t('annotation.layer')} ${layerNumber}`;
  emit('layer-create', defaultName);
};

const startRenameLayer = (layerId: string, currentName: string) => {
  editingLayerId.value = layerId;
  editingLayerName.value = currentName;
  activeMenuLayerId.value = null;
  hasSelectedText.value = false;
  // Focus and select handled by setEditInputRef callback
};

const saveLayerName = (layerId: string) => {
  if (editingLayerName.value.trim()) {
    emit('layer-rename', layerId, editingLayerName.value.trim());
  }
  editingLayerId.value = null;
  editingLayerName.value = '';
  hasSelectedText.value = false;
};

const cancelEditLayerName = () => {
  editingLayerId.value = null;
  editingLayerName.value = '';
  hasSelectedText.value = false;
};

const deleteLayer = (layerId: string) => {
  if (props.layers.length <= 1) {
    return; // Prevent deletion of last layer
  }
  notifyConfirm(t('annotation.confirmDeleteLayer'), () => {
    emit('layer-delete', layerId);
    activeMenuLayerId.value = null;
  });
};

const duplicateLayer = (layerId: string) => {
  emit('layer-duplicate', layerId);
  activeMenuLayerId.value = null;
};

const changeLayerColor = (layerId: string) => {
  selectedLayerForColor.value = layerId;
  showColorPicker.value = true;
  activeMenuLayerId.value = null;
};

const applyLayerColor = (color: string) => {
  if (selectedLayerForColor.value) {
    emit('layer-change-color', selectedLayerForColor.value, color);
  }
  showColorPicker.value = false;
  selectedLayerForColor.value = null;
};

const updateLayerOpacity = (layerId: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  const opacity = parseInt(target.value) / 100;
  emit('layer-change-opacity', layerId, opacity);
};

const moveLayerUp = (layerId: string) => {
  emit('layer-reorder', layerId, 'up');
  activeMenuLayerId.value = null;
};

const moveLayerDown = (layerId: string) => {
  emit('layer-reorder', layerId, 'down');
  activeMenuLayerId.value = null;
};

const isTopLayer = (layerId: string) => {
  const layer = props.layers.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer) return true;
  return layer.order === Math.max(...props.layers.map((l: AnnotationLayer) => l.order));
};

const isBottomLayer = (layerId: string) => {
  const layer = props.layers.find((l: AnnotationLayer) => l.id === layerId);
  if (!layer) return true;
  return layer.order === Math.min(...props.layers.map((l: AnnotationLayer) => l.order));
};

const toggleLayerMenu = (layerId: string) => {
  activeMenuLayerId.value = activeMenuLayerId.value === layerId ? null : layerId;
};

const mergeVisibleLayers = () => {
  const visibleLayerIds = props.layers.filter((l: AnnotationLayer) => l.visible).map((l: AnnotationLayer) => l.id);
  if (visibleLayerIds.length < 2) return;
  
  notifyConfirm(t('annotation.confirmMergeLayers'), () => {
    emit('layer-merge', visibleLayerIds);
  });
};

// Close menu when clicking outside
const closeMenus = () => {
  activeMenuLayerId.value = null;
};

// Close menus on outside click
if (typeof window !== 'undefined') {
  window.addEventListener('click', closeMenus);
}
</script>

<style scoped>
.layer-panel {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.layer-panel__header {
  padding: 16px;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
}

.layer-panel__header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.layer-panel__header h3 i {
  color: #007bff;
}

.layer-panel__header .btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
  transition: color 0.2s;
}

.layer-panel__header .btn-close:hover {
  color: #dc3545;
}

.layer-panel__content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.layer-panel__add-btn {
  width: 100%;
  padding: 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
}

.layer-panel__add-btn:hover {
  background: #0056b3;
}

.layer-panel__add-btn i {
  font-size: 14px;
}

.layer-panel__controls {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #dee2e6;
}

.layer-panel__controls button {
  flex: 1;
  padding: 8px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s;
}

.layer-panel__controls button:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}

.layer-panel__controls button.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.layer-panel__controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.layer-panel__controls button i {
  font-size: 12px;
}

.layer-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.layer-item {
  background: #f8f9fa;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.layer-item:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.layer-item--active {
  background: #e7f3ff;
  border-color: #007bff;
}

.layer-item--locked {
  opacity: 0.7;
}

.layer-item__action {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  transition: color 0.2s;
  flex-shrink: 0;
}

.layer-item__action:hover {
  color: #007bff;
}

.layer-item__name {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.layer-item__color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.layer-item__text {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.layer-item__count {
  font-size: 12px;
  color: #6c757d;
  flex-shrink: 0;
}

.layer-item__input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #007bff;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.layer-item__input:focus {
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.layer-item__actions {
  position: relative;
  flex-shrink: 0;
}

.layer-item__menu {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 14px;
  transition: color 0.2s;
}

.layer-item__menu:hover {
  color: #007bff;
}

.layer-item__opacity {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 6px;
  padding: 8px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.layer-item__opacity label {
  display: block;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.layer-item__opacity input[type="range"] {
  width: 100%;
  cursor: pointer;
}

.layer-menu {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.layer-menu button {
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #333;
  transition: background 0.2s;
}

.layer-menu button:hover:not(:disabled) {
  background: #f8f9fa;
}

.layer-menu button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.layer-menu button i {
  width: 16px;
  color: #6c757d;
}

.layer-menu__danger {
  color: #dc3545 !important;
}

.layer-menu__danger i {
  color: #dc3545 !important;
}

.layer-menu__danger:hover:not(:disabled) {
  background: #fff5f5 !important;
}

.layer-menu hr {
  margin: 4px 0;
  border: none;
  border-top: 1px solid #dee2e6;
}

.color-picker-dialog {
  background: white;
  border-radius: 12px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.color-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.color-picker-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.color-picker-header .btn-close {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 16px;
}

.color-picker-header .btn-close:hover {
  color: #dc3545;
}

.color-picker-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.color-preset-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.color-preset {
  width: 100%;
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.color-preset:hover {
  transform: scale(1.1);
  border-color: #333;
}

.color-picker-custom {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-picker-custom label {
  font-size: 14px;
  color: #333;
}

.color-picker-custom input[type="color"] {
  width: 60px;
  height: 40px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

/* Transitions */
.layer-fade-enter-active,
.layer-fade-leave-active {
  transition: all 0.3s ease;
}

.layer-fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.layer-fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.layer-fade-move {
  transition: transform 0.3s ease;
}
</style>
